<template>
  <div class="condition-group-builder">
    <div class="condition-group before">
      <slot name="before"></slot>
    </div>
    <div class="condition-group" v-for="(item, index) in conditionGroup.conditions" :key="getConditionKey(index, item)">
      <ConditionGroupBuilder
        v-if="!isCondition(item)"
        :value="item"
        :manager="manager"
        :nested="true"
        :disabled="disabled"
        @updated="onGroupUpdated"
        :context="context"
      />
      <ConditionButton
        v-if="isCondition(item)"
        :value="item"
        :manager="manager"
        :disabled="disabled"
        @deleted="onConditionDeleted(index)"
        @updated="(updatedItem: ConditionViewModel) => onConditionUpdated(index, updatedItem)"
        @config-opened="emit('config-opened')"
        @config-closed="emit('config-closed')"
        :class="{ left: !nested || index === 0 }"
      />

      <AddCondition
        v-if="showMakeFilterGroupOption(item)"
        :disabled="disabled || hasAnyInvalidConditions()"
        :tooltip="
          !hasAnyInvalidConditions() ? `Make ${context} group` : `Fix invalid ${context}s before adding another`
        "
        :available-conditions="manager.visibleConditions.value"
        @added="(addedItem: ConditionViewModel) => makeFilterGroup(index, <ConditionViewModel>item, addedItem)"
        @click.stop
      >
        <button class="button add-icon" :disabled="disabled">
          <AddIcon />
        </button>
      </AddCondition>

      <AddCondition
        v-if="showAddToFilterGroupOption(item, index)"
        :available-conditions="manager.visibleConditions.value"
        :disabled="disabled || hasAnyInvalidConditions()"
        :tooltip="
          !hasAnyInvalidConditions() ? `Add to ${context} group` : `Fix invalid ${context}s before adding another`
        "
        @added="(addedItem: ConditionViewModel) => addToFilterGroup(index, addedItem)"
      >
        <button class="button add-icon" :disabled="disabled">
          <AddIcon />
        </button>
      </AddCondition>

      <JoinOperatorPicker
        class="group-type"
        :class="{ 'top-level-grouping': !nested, hover: operatorButtonHover }"
        v-if="!isLastConditionInList(index)"
        :disabled="disabled"
        :value="conditionGroup.type"
        @updated-operator="operatorTypeUpdated"
        @updated-hover-state="updateOperatorHoverState"
      />
    </div>

    <AddCondition
      v-if="!nested"
      :available-conditions="manager.visibleConditions.value || []"
      :disabled="disabled || hasAnyInvalidConditions()"
      @added="onConditionAdded"
      class="add-filter"
      :class="{ 'has-conditions': numberOfConditions > 0 }"
    >
      <a>
        <AddIcon />
        <span class="add-label">Add {{ context }}</span>
      </a>
    </AddCondition>
  </div>
</template>

<script lang="ts" setup>
import { ConditionGroupViewModel } from './models/conditionGroupViewModel';
import { IConditionsManager } from './models/conditionsManager';
import AddCondition from './AddCondition.vue';
import ConditionButton from './ConditionButton.vue';
import AddIcon from 'vue-material-design-icons/Plus.vue';
import JoinOperatorPicker from '@/features/contacts/components/JoinOperatorPicker.vue';
import { OperatorType } from '@/features/contacts/models/operatorTypes';
import { PropType, ref, watch, computed } from 'vue';
import { ConditionViewModel } from '@/shared/components/conditions/models/conditionViewModel';
import { ConditionGroup } from '@/shared/components/conditions/models/conditionGroup';

export interface ConditionGroupUpdate {
  conditionGroup: ConditionGroupViewModel;
  invalid: boolean;
}

const props = defineProps({
  value: {
    type: Object as PropType<ConditionGroupViewModel>,
    required: true,
  },
  manager: {
    type: Object as PropType<IConditionsManager>,
    required: true,
  },
  nested: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  context: {
    type: String,
    default: 'filter',
  },
});

const emit = defineEmits<{
  (e: 'updated', update: any): void;
  (e: 'config-opened'): void;
  (e: 'config-closed'): void;
}>();

const conditionGroup = ref<ConditionGroupViewModel>({ type: 'and', conditions: [] });
const operatorButtonHover = ref(false);

watch(
  () => props.value,
  (value: ConditionGroupViewModel) => (conditionGroup.value = value),
  { immediate: true },
);

const numberOfConditions = computed(() => {
  if (!conditionGroup.value?.conditions) {
    return 0;
  }

  return conditionGroup.value.conditions.length;
});

function getConditionKey(index: number, item: ConditionViewModel | ConditionGroupViewModel) {
  if (isCondition(item)) {
    return `${index}_${item.attribute}_${item.operation}_${item.value}`;
  }

  return `${index}_group`;
}

function isLastConditionInList(index: number) {
  return index === numberOfConditions.value - 1;
}

function isCondition(item: ConditionViewModel | ConditionGroupViewModel): item is ConditionViewModel {
  return ConditionGroup.isCondition(item);
}

function showMakeFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel) {
  return !props.nested && isCondition(item);
}

function showAddToFilterGroupOption(item: ConditionViewModel | ConditionGroupViewModel, index: number) {
  return props.nested && isCondition(item) && index === numberOfConditions.value - 1;
}

function onConditionAdded(newItem: ConditionViewModel) {
  newItem.active = true;
  newItem.invalid = false;
  newItem.isNew = true;
  conditionGroup.value.conditions.push(newItem);
}

function onConditionUpdated(index: number, item: ConditionViewModel) {
  item.active = false;
  conditionGroup.value.conditions[index] = item;

  emitUpdatedEvent();
}

function onConditionDeleted(index: number) {
  conditionGroup.value.conditions.splice(index, 1);

  emitUpdatedEvent();
}

function makeFilterGroup(index: number, item: ConditionViewModel, newItem: ConditionViewModel) {
  // replace the condition with a condition group containing existing and new item
  newItem.active = true;
  conditionGroup.value.conditions[index] = {
    type: 'and',
    conditions: [item, newItem],
  };
}

function addToFilterGroup(index: number, newItem: ConditionViewModel) {
  newItem.active = true;
  conditionGroup.value.conditions.push(newItem);
}

function onGroupUpdated() {
  // a child group has been updated - check if any groups have only got one condition left,
  // if so convert them back to a condition from a filter group
  conditionGroup.value.conditions = conditionGroup.value.conditions.map((c: ConditionGroupViewModel, index: number) => {
    if (isCondition(c)) {
      c.active = false;
      return c;
    }

    c = c as ConditionGroupViewModel;
    if (c.conditions.length === 1) {
      return c.conditions[0];
    }

    return c;
  });

  emitUpdatedEvent();
}

function operatorTypeUpdated(operatorType: OperatorType) {
  conditionGroup.value.type = operatorType;
  onGroupUpdated();
}

function updateOperatorHoverState(hover: boolean) {
  operatorButtonHover.value = hover;
}

function hasAnyInvalidConditions() {
  return ConditionGroup.isGroupInvalid(conditionGroup.value);
}

function emitUpdatedEvent() {
  if (props.nested) {
    emit('updated', conditionGroup.value);
  } else {
    emit('updated', {
      conditionGroup: conditionGroup.value,
      invalid: hasAnyInvalidConditions(),
    } as ConditionGroupUpdate);
  }
}
</script>

<style scoped lang="scss">
.condition-group-builder {
  display: flex;
  flex-wrap: wrap;
  position: relative;
  align-items: center;
  box-sizing: border-box;
  row-gap: 5px;
}

.add-filter {
  display: flex;
  align-items: center;
  height: 2.25em;

  &.has-conditions {
    margin-left: 1.5em;
  }
}

:deep(.condition-group) {
  display: flex;
  flex-wrap: wrap;

  .condition-group {
    margin-bottom: 0; // don't set margin on nested ones
  }

  &.before {
    .dropdown-trigger {
      margin-right: 1.5em;
    }

    > div {
      margin-right: 1.5em;
    }
  }
}

:deep(.button) {
  background-color: #eee;
  border: solid 1px #eee;
  color: inherit;
  min-width: 0;
  cursor: pointer;

  &:hover {
    color: inherit;
    background-color: #ccc;
    border: solid 1px #ccc;
  }

  &:focus {
    color: inherit;
    border-color: #ccc;
    outline: none;
  }

  .material-design-icon {
    margin-bottom: 2px;
  }
}

:deep(.group-type) {
  margin: 0 0.15em;

  &.hover {
    .button {
      color: inherit;
      background-color: #ccc;
      border: solid 1px #ccc;
    }
  }

  &.top-level-grouping {
    margin-left: 0 0.5em;

    &.hover {
      .button {
        color: #3273dc;
      }
    }

    .button {
      border: solid 1px transparent;
      background-color: transparent;

      &.dropdown-active {
        color: #3273dc;
        border-color: transparent;
        outline: none;
      }

      &:focus {
        color: #3273dc;
        border-color: transparent;
        outline: none;
      }
    }
  }
}

.add-icon {
  margin-left: 0.15em;
  border-radius: 0 4px 4px 0;
}

:deep(.condition-button-container) {
  .condition-button {
    border-radius: 0;
  }

  &.left {
    .condition-button {
      border-radius: 4px 0 0 4px;
    }
  }
}
</style>
